import { useNode, useEditor, Element } from "@craftjs/core";
import React, { useRef } from "react";
import { Box, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import { useResponsive } from "../../hooks/useResponsive";
import { heroStyles, getHeroMinHeight } from "../../config/styleConfig";
import { Text } from "./Text";
import { Button } from "./Button";
import { selectedOutlineSx, labelStyle, addButtonStyle, removeButtonStyle } from "../../config/settingsStyles";
import { ImageUpload } from "../ui/ImageUpload";
import { ImageCropPicker } from "../ui/ImageCropPicker";
import {
  TextField,
  TextareaField,
  SelectField,
  ColorField,
  SliderField,
  SettingsSection,
  SettingsRow,
} from "../ui/form/SettingsField";

export interface HeroProps {
  title: string;
  subtitle?: string;
  backgroundImage?: string;
  backgroundPosition?: string;
  backgroundZoom?: number;
  backgroundColor?: string;
  overlayColor?: string;
  overlayOpacity?: number;
  minHeight?: number;
  buttonText?: string;
  buttonLink?: string;
  buttonVariant?: "primary" | "secondary" | "outline" | "white";
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  secondaryButtonVariant?: "primary" | "secondary" | "outline" | "white";
  textAlign?: "left" | "center" | "right";
  titleColor?: string;
  subtitleColor?: string;
  maxWidth?: number;
  paddingX?: number;
  paddingTop?: number;
  paddingBottom?: number;
  textFontFamily?: string;
}

export const Hero = ({
  title = "Welcome to Our Clinic",
  subtitle,
  backgroundImage,
  backgroundPosition = "50% 50%",
  backgroundZoom = 1,
  backgroundColor = "#1e3a5f",
  overlayColor = "#000000",
  overlayOpacity = 0.4,
  minHeight = 500,
  buttonText,
  buttonLink = "#",
  buttonVariant = "primary",
  secondaryButtonText,
  secondaryButtonLink = "#",
  secondaryButtonVariant = "outline",
  textAlign = "center",
  titleColor = "#ffffff",
  subtitleColor = "#e5e7eb",
  maxWidth = 896,
  paddingX = 60,
  paddingTop = 48,
  paddingBottom = 48,
  textFontFamily,
}: HeroProps) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const { enabled: editorEnabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useResponsive(containerRef as React.RefObject<HTMLElement>);

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (editorEnabled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const safeMinHeight = minHeight || heroStyles.minHeight.desktop;

  // Use shared style config for hero
  const currentStyles = isMobile
    ? {
        minHeight: getHeroMinHeight(safeMinHeight, true),
        title: heroStyles.title.mobile,
        subtitle: heroStyles.subtitle.mobile,
        padding: heroStyles.padding.mobile,
        button: heroStyles.button.mobile,
      }
    : {
        minHeight: getHeroMinHeight(safeMinHeight, false),
        title: heroStyles.title.desktop,
        subtitle: heroStyles.subtitle.desktop,
        padding: heroStyles.padding.desktop,
        button: heroStyles.button.desktop,
      };

  const getButtonStyles = (variant?: string) => {
    const v = variant || buttonVariant;
    const baseStyles = {
      display: "inline-block",
      px: `${currentStyles.button.px}px`,
      py: `${currentStyles.button.py}px`,
      borderRadius: 2,
      fontWeight: 500,
      fontSize: isMobile ? "0.875rem" : "1rem",
      textDecoration: "none",
      transition: "opacity 0.2s",
      wordWrap: "break-word" as const,
      overflowWrap: "break-word" as const,
      "&:hover": { opacity: 0.9 },
    };

    switch (v) {
      case "white":
        return { ...baseStyles, bgcolor: "#ffffff", color: "#1f2937" };
      case "outline":
        return { ...baseStyles, bgcolor: "transparent", border: "2px solid white", color: "#ffffff" };
      case "secondary":
        return { ...baseStyles, background: "var(--color-secondary, #10b981)", color: "#ffffff" };
      default:
        return {
          ...baseStyles,
          background: "linear-gradient(135deg, var(--color-primary, #3b82f6) 0%, var(--color-secondary, #10b981) 100%)",
          color: "#ffffff",
        };
    }
  };

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) {
          connect(drag(ref));
          (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = ref;
        }
      }}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: currentStyles.minHeight,
        bgcolor: backgroundImage ? "transparent" : backgroundColor,
        overflow: "hidden",
        ...(selected && selectedOutlineSx),
      }}
    >
      {backgroundImage && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: backgroundPosition || "50% 50%",
            backgroundRepeat: "no-repeat",
            transform: `scale(${backgroundZoom || 1})`,
            transformOrigin: backgroundPosition || "50% 50%",
          }}
        />
      )}
      {backgroundImage && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: overlayColor,
            opacity: overlayOpacity,
          }}
        />
      )}

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          maxWidth: maxWidth ? `${maxWidth}px` : undefined,
          px: `${paddingX}px`,
          pt: `${paddingTop}px`,
          pb: `${paddingBottom}px`,
          textAlign,
        }}
      >
        <Element
          id="hero-title"
          is={Text}
          text={title}
          fontSize={48}
          fontWeight="bold"
          tagName="h1"
          color={titleColor}
          textAlign={textAlign}
          marginBottom={16}
          fontFamily={textFontFamily}
        />
        <Element
          id="hero-subtitle"
          is={Text}
          text={subtitle ?? ""}
          fontSize={20}
          fontWeight="normal"
          tagName="p"
          color={subtitleColor}
          textAlign={textAlign}
          marginBottom={32}
          fontFamily={textFontFamily}
        />
        <Box sx={{ display: "flex", gap: 2, justifyContent: textAlign, flexWrap: "wrap" }}>
          <Element
            id="hero-button"
            is={Button}
            text={buttonText || "Book Appointment"}
            link={buttonLink || "/contact"}
            variant="primary"
            size="md"
            backgroundColor="var(--color-primary, #3b82f6)"
            textColor="#ffffff"
            borderRadius={8}
          />
        </Box>
      </Box>
    </Box>
  );
};

export const HeroSettings = () => {
  const {
    actions: { setProp },
    title,
    subtitle,
    backgroundImage,
    backgroundPosition,
    backgroundZoom,
    backgroundColor,
    overlayColor,
    overlayOpacity,
    minHeight,
    buttonText,
    buttonLink,
    buttonVariant,
    secondaryButtonText,
    secondaryButtonLink,
    secondaryButtonVariant,
    textAlign,
    titleColor,
    subtitleColor,
    maxWidth,
    paddingX,
    paddingTop,
    paddingBottom,
  } = useNode((node) => ({
    title: node.data.props.title,
    subtitle: node.data.props.subtitle,
    backgroundImage: node.data.props.backgroundImage,
    backgroundPosition: node.data.props.backgroundPosition,
    backgroundZoom: node.data.props.backgroundZoom,
    backgroundColor: node.data.props.backgroundColor,
    overlayColor: node.data.props.overlayColor,
    overlayOpacity: node.data.props.overlayOpacity,
    minHeight: node.data.props.minHeight,
    buttonText: node.data.props.buttonText,
    buttonLink: node.data.props.buttonLink,
    buttonVariant: node.data.props.buttonVariant,
    secondaryButtonText: node.data.props.secondaryButtonText,
    secondaryButtonLink: node.data.props.secondaryButtonLink,
    secondaryButtonVariant: node.data.props.secondaryButtonVariant,
    textAlign: node.data.props.textAlign,
    titleColor: node.data.props.titleColor,
    subtitleColor: node.data.props.subtitleColor,
    maxWidth: node.data.props.maxWidth,
    paddingX: node.data.props.paddingX,
    paddingTop: node.data.props.paddingTop,
    paddingBottom: node.data.props.paddingBottom,
  }));

  return (
    <SettingsSection>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        💡 คลิกที่ตัวอักษรบน canvas เพื่อแก้ไข text, สี, ขนาด
      </Typography>

      <ImageUpload
        label="Background Image"
        value={backgroundImage || ""}
        onChange={(url) => setProp((props: HeroProps) => (props.backgroundImage = url || undefined))}
        placeholder="Enter URL or upload image"
        description="Recommended size: 1920 x 800 px"
      />

      {backgroundImage && (
        <ImageCropPicker
          backgroundImage={backgroundImage}
          backgroundPosition={backgroundPosition}
          backgroundZoom={backgroundZoom}
          onPositionChange={(v) => setProp((props: HeroProps) => (props.backgroundPosition = v))}
          onZoomChange={(v) => setProp((props: HeroProps) => (props.backgroundZoom = v))}
        />
      )}

      <ColorField
        label="Background Color (if no image)"
        value={backgroundColor}
        onChange={(v) => setProp((props: HeroProps) => (props.backgroundColor = v))}
      />

      {backgroundImage && (
        <>
          <ColorField
            label="Overlay Color"
            value={overlayColor}
            onChange={(v) => setProp((props: HeroProps) => (props.overlayColor = v))}
          />
          <SliderField
            label="Overlay Opacity"
            value={overlayOpacity}
            onChange={(v) => setProp((props: HeroProps) => (props.overlayOpacity = v))}
            min={0}
            max={1}
            step={0.1}
          />
        </>
      )}

      <SliderField
        label="Min Height"
        value={minHeight}
        onChange={(v) => setProp((props: HeroProps) => (props.minHeight = v))}
        min={300}
        max={800}
        unit="px"
      />

      <SliderField
        label="Content Max Width"
        value={maxWidth || 896}
        onChange={(v) => setProp((props: HeroProps) => (props.maxWidth = v))}
        min={0}
        max={1600}
        unit="px"
      />

      <SliderField
        label="Padding X"
        value={paddingX ?? 60}
        onChange={(v) => setProp((props: HeroProps) => (props.paddingX = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Padding Top"
        value={paddingTop ?? 48}
        onChange={(v) => setProp((props: HeroProps) => (props.paddingTop = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Padding Bottom"
        value={paddingBottom ?? 48}
        onChange={(v) => setProp((props: HeroProps) => (props.paddingBottom = v))}
        min={0}
        max={120}
        unit="px"
      />

      <Box>
        <Typography variant="body2" gutterBottom>
          Text Align
        </Typography>
        <ToggleButtonGroup
          value={textAlign || "center"}
          exclusive
          onChange={(_, value) => {
            if (value !== null) {
              setProp((props: HeroProps) => (props.textAlign = value as HeroProps["textAlign"]));
            }
          }}
          size="small"
          fullWidth
        >
          <ToggleButton value="left" sx={{ flex: 1 }}>
            <FormatAlignLeftIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="center" sx={{ flex: 1 }}>
            <FormatAlignCenterIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="right" sx={{ flex: 1 }}>
            <FormatAlignRightIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        💡 คลิกที่ปุ่มบน canvas เพื่อแก้ไข text, link, สี
      </Typography>
    </SettingsSection>
  );
};

Hero.craft = {
  displayName: "Hero Section",
  isCanvas: true,
  props: {
    title: "Welcome to Our Clinic",
    subtitle: "Providing excellent healthcare services with compassion and expertise",
    backgroundImage: undefined,
    backgroundPosition: "50% 50%",
    backgroundZoom: 1,
    backgroundColor: "#1e3a5f",
    overlayColor: "#000000",
    overlayOpacity: 0.4,
    minHeight: 500,
    buttonText: "Book Appointment",
    buttonLink: "/contact",
    buttonVariant: "primary",
    secondaryButtonText: undefined,
    secondaryButtonLink: undefined,
    secondaryButtonVariant: "outline",
    textAlign: "center",
    titleColor: "#ffffff",
    subtitleColor: "#e5e7eb",
    maxWidth: 896,
    paddingX: 60,
    paddingTop: 48,
    paddingBottom: 48,
    textFontFamily: undefined,
  },
  rules: {
    canMoveIn: () => false,
    canMoveOut: () => false,
  },
  related: {
    settings: HeroSettings,
  },
};
